import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { ThemeCustomizerProvider } from '@/lib/theme-customizer';
import './index.css';
import './styles/shimmer.css';
import './App.css';
import './lib/sentry';
import { SITE_TITLE } from '@/lib/site-meta';
import { AppTree } from './app-tree.tsx';
import { installFluidRouteTransitions } from '@/lib/fluid-route-transitions';

document.title = SITE_TITLE;

const queryClient = new QueryClient();
const isDevelopment = import.meta.env.DEV;

function logFrontendIncident(error: unknown, context: string, details?: unknown) {
  if (!isDevelopment) {
    return;
  }

  console.error(`[frontend:${context}]`, error, details);
}

const router = createBrowserRouter([
  { path: '*', element: <AppTree /> },
]);
installFluidRouteTransitions(router);

const container = document.getElementById('root');

if (!container) {
  throw new Error('Root container #root not found.');
}

createRoot(container, {
  onCaughtError: Sentry.reactErrorHandler((error, errorInfo, eventId) => {
    logFrontendIncident(error, 'caught-error', {
      componentStack: errorInfo.componentStack,
      eventId,
    });
  }),
  onRecoverableError: Sentry.reactErrorHandler((error, errorInfo, eventId) => {
    logFrontendIncident(error, 'recoverable-error', {
      componentStack: errorInfo.componentStack,
      eventId,
    });
  }),
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo, eventId) => {
    logFrontendIncident(error, 'uncaught-error', {
      componentStack: errorInfo.componentStack,
      eventId,
    });
  }),
}).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeCustomizerProvider>
        <RouterProvider router={router} />
      </ThemeCustomizerProvider>
    </QueryClientProvider>
  </StrictMode>,
);
